import React, { useEffect, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FilterState } from '@/services/filters/types';
import { FilterOption } from '@/services/filters/commonTypes';
import { URL_QUERY_VALUE_SEPARATOR } from '@/utils/constants';
import { FILTER_VALUE_SEPARATOR, FILTER_VALUE_SEPARATOR_ENCODED } from '@/constants/filters';
import { SearchInput } from '@/components/common/filters/SearchInput';

import { SelectAll } from './components/SelectAll';
import { CheckboxListItem } from './components/CheckboxListItem';
import { useGetMergedItemsToRender } from './hooks/useGetMergedItemsToRender';

import s from './GenericCheckboxList.module.scss';

export interface GenericCheckboxListProps {
  /**
   * Label displayed above the search input
   */
  label?: string;

  hint?: React.ReactNode;

  /**
   * URL parameter key to manage
   */
  paramKey: string;

  /**
   * Filter store to use (e.g., useFilterStore, useTeamFilterStore)
   */
  filterStore: () => FilterState;

  /**
   * Placeholder text for search input
   */
  placeholder?: string;

  /**
   * Number of items to show by default
   */
  defaultItemsToShow?: number;

  /**
   * Number of items to show when search input is not empty
   */
  searchResultsToShow?: number;

  /**
   * Hook to fetch data based on search input
   * @param input - Search query string
   * @param limit - Optional limit for results
   * @returns Object with optional data array
   */
  useGetDataHook: (input: string, limit?: number) => { data?: FilterOption[] };

  /**
   * Optional callback when selected values change
   * @param paramKey - The parameter key
   * @param values - Array of selected values (strings)
   */
  onChange?: (paramKey: string, values: string[]) => void;

  /**
   * Optional callback when search input changes
   * @param searchQuery - The search query string
   */
  onSearch?: (searchQuery: string) => void;

  /**
   * Optional callback when "Select All" is clicked
   * @param wasChecked - Whether the items were checked before clicking (true = deselecting, false = selecting)
   */
  onSelectAll?: (wasChecked: boolean) => void;

  /**
   * Flag to clear search input (external control)
   */
  shouldClearSearch?: boolean;

  /**
   * Custom class name for the container
   */
  className?: string;

  hideSearch?: boolean;

  disableSorting?: boolean;
}

/**
 * Generic Checkbox List with Search
 *
 * Reusable checkbox list component with search functionality.
 * Works with any filter store created via createFilterStore.
 *
 * @example
 * ```tsx
 * // Simple usage
 * <GenericCheckboxList
 *   label="Search topics"
 *   paramKey="topics"
 *   placeholder="E.g. AI, Blockchain..."
 *   filterStore={useFilterStore}
 *   useGetDataHook={useGetTopics}
 *   defaultItemsToShow={5}
 * />
 *
 * // With onChange callback
 * <GenericCheckboxList
 *   label="Search roles"
 *   paramKey="roles"
 *   placeholder="E.g. Engineer, Designer..."
 *   filterStore={useFilterStore}
 *   useGetDataHook={useGetRoles}
 *   defaultItemsToShow={4}
 *   onChange={(key, values) => {
 *     console.log('Selected:', values);
 *   }}
 * />
 * ```
 */
export function GenericCheckboxList(props: GenericCheckboxListProps) {
  const {
    label,
    hint,
    paramKey,
    filterStore,
    placeholder,
    useGetDataHook,
    defaultItemsToShow,
    searchResultsToShow,
    onChange,
    onSearch,
    onSelectAll,
    shouldClearSearch,
    className,
    hideSearch,
    disableSorting,
  } = props;

  const [searchValue, setSearchValue] = useState('');

  // Handle search change with optional callback
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const { params, setParam } = filterStore();

  // Fetch data based on search
  const { data = [] } = useGetDataHook(searchValue);

  // Get initial values from URL parameters
  const selectedValues = useMemo(() => {
    const paramValue = params.get(paramKey);

    if (!paramValue) {
      return [];
    }

    return paramValue.split(URL_QUERY_VALUE_SEPARATOR).map((raw) => {
      const value = raw.trim().replaceAll(FILTER_VALUE_SEPARATOR_ENCODED, FILTER_VALUE_SEPARATOR);
      return {
        value,
        label: data?.find((item) => item.value === value)?.label || value,
      };
    });
  }, [params, paramKey, data]);

  // Merge backend data with selected values
  const itemsToRender = useGetMergedItemsToRender({
    beData: data,
    selectedData: selectedValues,
    searchValue,
    defaultItemsToShow,
    disableSorting,
    searchResultsToShow,
  });

  // React Hook Form setup
  const methods = useForm<Record<string, FilterOption[]>>({
    defaultValues: { [paramKey]: selectedValues },
  });

  const { watch, setValue } = methods;
  const filterValues = watch(paramKey);

  // Sync form values to URL parameters (only when form values actually change)
  useEffect(() => {
    // Skip if filterValues is undefined (initial render before form is ready)
    if (filterValues === undefined) return;

    if (filterValues && filterValues.length > 0) {
      const valuesArr = filterValues.map((item) => item.value);
      const values = valuesArr
        .map((v) => v.replaceAll(FILTER_VALUE_SEPARATOR, FILTER_VALUE_SEPARATOR_ENCODED))
        .join(URL_QUERY_VALUE_SEPARATOR);

      // Only update if the value actually changed
      const currentValue = params.get(paramKey);
      if (currentValue !== values) {
        setParam(paramKey, values);

        // Call onChange callback if provided
        if (onChange) {
          onChange(paramKey, valuesArr);
        }
      }
    } else if (filterValues.length === 0) {
      // Only clear if there was a value before
      if (params.get(paramKey)) {
        setParam(paramKey, undefined);

        // Call onChange with empty array
        if (onChange) {
          onChange(paramKey, []);
        }
      }
    }
    // setParam and onChange are stable references, don't need them in deps
    // params is intentionally included to check current value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey, filterValues]);

  // Handle external search clear
  useEffect(() => {
    if (shouldClearSearch) {
      setSearchValue('');
    }
  }, [shouldClearSearch]);

  return (
    <FormProvider {...methods}>
      <div className={className}>
        {label && <div className={s.label}>{label}</div>}
        {hint && <div className={s.hint}>{hint}</div>}
        {!hideSearch && <SearchInput value={searchValue} onChange={handleSearchChange} placeholder={placeholder} />}
        <div className={s.list}>
          {!!searchValue && (
            <SelectAll
              data={data}
              paramKey={paramKey}
              setValue={setValue}
              selected={selectedValues}
              onSelectAll={onSelectAll}
            />
          )}
          {itemsToRender.map((item) => {
            return (
              <CheckboxListItem
                key={item.value}
                item={item}
                values={selectedValues}
                setValue={setValue}
                paramKey={paramKey}
              />
            );
          })}
        </div>
      </div>
    </FormProvider>
  );
}
